import React from 'react';
import { motion } from 'framer-motion';
import { Briefcase, Calendar, MapPin } from 'lucide-react';
import { experience } from '../data/mock';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';

const ExperienceSection = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -30 },
    visible: { opacity: 1, x: 0 }
  };

  return (
    <section id="experience" className="py-20 px-4 sm:px-6 lg:px-8 bg-muted/30">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={containerVariants}
        >
          {/* Section Header */}
          <div className="text-center mb-16">
            <motion.div variants={itemVariants} className="inline-block">
              <Badge variant="outline" className="mb-4">
                <Briefcase className="w-3 h-3 mr-1" />
                Career Path
              </Badge>
            </motion.div>
            <motion.h2
              variants={itemVariants}
              className="text-3xl sm:text-4xl font-bold mb-4"
            >
              Professional Experience
            </motion.h2>
            <motion.p
              variants={itemVariants}
              className="text-lg text-muted-foreground max-w-2xl mx-auto"
            >
              A journey through impactful projects and continuous growth
            </motion.p>
          </div>

          {/* Timeline */}
          <div className="relative">
            {/* Center Line */}
            <div className="hidden lg:block absolute left-1/2 transform -translate-x-1/2 w-0.5 h-full bg-border" />

            <div className="space-y-12">
              {experience.map((exp, index) => (
                <motion.div
                  key={exp.id}
                  variants={itemVariants}
                  className={`relative lg:grid lg:grid-cols-2 lg:gap-8 ${
                    index % 2 === 0 ? '' : 'lg:grid-flow-dense'
                  }`}
                >
                  {/* Timeline Dot */}
                  <div className="hidden lg:block absolute left-1/2 top-8 transform -translate-x-1/2 w-4 h-4 rounded-full bg-blue-500 border-4 border-background z-10" />

                  {/* Content */}
                  <div className={index % 2 === 0 ? 'lg:col-start-2' : 'lg:col-start-1'}>
                    <Card className="hover:shadow-lg transition-all">
                      <CardContent className="p-6">
                        <div className="flex items-start space-x-4">
                          <div className="p-2 rounded-lg bg-blue-500/10 flex-shrink-0">
                            <Briefcase className="w-5 h-5 text-blue-500" />
                          </div>
                          <div className="flex-1">
                            <h3 className="text-lg font-bold mb-1">{exp.role}</h3>
                            <p className="text-sm font-semibold text-blue-500 mb-2">
                              {exp.company}
                            </p>
                            <div className="flex flex-wrap gap-3 mb-3 text-xs text-muted-foreground">
                              <div className="flex items-center">
                                <Calendar className="w-3 h-3 mr-1" />
                                {exp.period}
                              </div>
                              <div className="flex items-center">
                                <MapPin className="w-3 h-3 mr-1" />
                                {exp.location}
                              </div>
                            </div>
                            <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                              {exp.description}
                            </p>
                            <div className="flex flex-wrap gap-2">
                              {exp.technologies.slice(0, 6).map((tech) => (
                                <Badge key={tech} variant="secondary" className="text-xs">
                                  {tech}
                                </Badge>
                              ))}
                              {exp.technologies.length > 6 && (
                                <Badge variant="secondary" className="text-xs">
                                  +{exp.technologies.length - 6}
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ExperienceSection;
